/**
 * ClientOnboardingAccountType Component
 * Ask client if they are registering as a business or individual
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OnboardingProgressBar } from '../../components/ui';
import { useBeforeUnload } from '../../hooks';
import publicTokenService from '../../services/publicTokenService';
import styles from './ClientOnboarding.module.css';

const ClientOnboardingAccountType = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [selectedType, setSelectedType] = useState('');
    const [isSaved, setIsSaved] = useState(true);
    const [isTestMode] = useState(sessionStorage.getItem('onboarding_test_mode') === 'true');

    // Load saved data on mount
    useEffect(() => {
        const saved = sessionStorage.getItem(`onboarding_account_type_${token}`);
        if (saved) {
            setSelectedType(saved);
        }
    }, [token]);

    // Track unsaved changes
    useEffect(() => {
        const saved = sessionStorage.getItem(`onboarding_account_type_${token}`);
        setIsSaved(selectedType === saved || selectedType === '');
    }, [selectedType, token]);

    // Warn before leaving if unsaved changes
    useBeforeUnload(!isSaved, 'You have unsaved changes. Please save your progress before leaving.');

    const handleSaveAndExit = async () => {
        if (selectedType) {
            try {
                // Save to backend (step 2: Account Type)
                await publicTokenService.saveProgress(token, {
                    stepNumber: 2,
                    stepData: { accountType: selectedType },
                    markStepComplete: false,
                    moveToNextStep: false
                });
                
                // Also save to session storage for local resume
                sessionStorage.setItem(`onboarding_account_type_${token}`, selectedType);
                setIsSaved(true);
                alert('Your progress has been saved. You can return using the same link to continue.');
            } catch (error) {
                console.error('Failed to save progress:', error);
                alert('Failed to save progress. Please try again.');
            }
        } else {
            alert('Please select an account type before saving.');
        }
    };

    const handleContinue = async () => {
        if (selectedType) {
            try {
                // Save to backend and mark step complete (step 2: Account Type)
                await publicTokenService.saveProgress(token, {
                    stepNumber: 2,
                    stepData: { accountType: selectedType },
                    markStepComplete: true,
                    moveToNextStep: true
                });
                
                // Store in session storage
                sessionStorage.setItem(`onboarding_account_type_${token}`, selectedType);
                setIsSaved(true);
                
                // Navigate to business info page for both business and individual
                navigate(`/client-onboarding/${token}/business-info`);
            } catch (error) {
                console.error('Failed to save progress:', error);
                alert('Failed to save. Please try again.');
            }
        }
    };

    const handleGoBack = () => {
        navigate(`/client-onboarding/${token}/terms`);
    };

    const accountTypes = [
        {
            id: 'business',
            icon: '🏢',
            title: 'Business Entity',
            description: 'Registering on behalf of a company, organization, or institution',
            examples: 'Corporation, LLC, Partnership, Non-Profit, etc.'
        },
        {
            id: 'individual',
            icon: '👤',
            title: 'Individual',
            description: 'Registering as a private individual or sole proprietor',
            examples: 'Personal account, Independent contractor, Consultant'
        }
    ];

    return (
        <React.Fragment>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.logoHeader}>
                        <img 
                            src="/Alt Logo Flat.png" 
                            alt="WETLAB" 
                            className={styles.logoHeaderImg}
                        />
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.accountTypeContainer}>
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>Account Type</h1>
                            <p className={styles.subtitle}>
                                Please select the type of account you would like to create
                            </p>
                        </div>

                        <div className={styles.form}>
                            <div className={styles.optionsContainer}>
                                {accountTypes.map((type) => (
                                    <div
                                        key={type.id}
                                        className={`${styles.optionCard} ${selectedType === type.id ? styles.selected : ''}`}
                                        onClick={() => setSelectedType(type.id)}
                                    >
                                        <div className={styles.optionIcon}>{type.icon}</div>
                                        <h3 className={styles.optionTitle}>{type.title}</h3>
                                        <p className={styles.optionDescription}>{type.description}</p>
                                        <p className={styles.optionExamples}>{type.examples}</p>
                                        <div className={styles.radioButton}>
                                            <input
                                                type="radio"
                                                name="accountType"
                                                value={type.id}
                                                checked={selectedType === type.id}
                                                onChange={() => setSelectedType(type.id)}
                                                className={styles.radio}
                                            />
                                            <span className={styles.radioLabel}>
                                                {selectedType === type.id ? 'Selected' : 'Select'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.infoBox}>
                                <div className={styles.infoIcon}>ℹ️</div>
                                <div className={styles.infoContent}>
                                    <strong>Not sure which to choose?</strong> If you're ordering services for a company 
                                    or organization, select "Business Entity". If you're ordering for yourself as an 
                                    individual, select "Individual".
                                </div>
                            </div>

                            <div className={styles.actions}>
                        <button
                            onClick={handleGoBack}
                            className={styles.backButton}
                        >
                            ← Go Back
                        </button>
                        <div className={styles.rightActions}>
                            <button
                                onClick={handleSaveAndExit}
                                className={styles.saveButton}
                                disabled={!selectedType}
                            >
                                💾 Save and Continue Later
                            </button>
                            <button
                                onClick={handleContinue}
                                className={styles.continueButton}
                                disabled={!selectedType}
                            >
                                Continue →
                            </button>
                        </div>
                    </div>
                        </div>
                    </div>
                </div>

                <div className={styles.footer}>
                    <p className={styles.footerText}>
                        Questions? Contact us at <a href="mailto:support@wetlab.com">support@wetlab.com</a>
                    </p>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ClientOnboardingAccountType;
